import React from 'react';

const PaymentMade: React.FC = () => {
  return (
    <div className="max-w-6xl mx-auto space-y-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Payment Made</h1>
        <p className="text-gray-600">
            Payment Made refers to recording money paid to a supplier or vendor for goods and services purchased.
            It ensures proper expense tracking, accurate cash/bank balance management, and vendor account reconciliation.
        </p>
      </div>

      {/* Payment Receive Overview */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Payment Receive Overview</h2>
        <p className="text-gray-600 mb-4">
          Payment Mase in DAS represent amounts owed to supplier for goods or services received. Payment can be created
          directly or generated from purchase orders when items are received. The system manages the complete
          payment receive lifecycle from creation to payment processing and customer credit management.
        </p>

        <div className="mb-6">
          <img
            src="/images/purchase/payment-made/list.png"
            alt="Payment Receive List View"
            className="w-full border border-gray-300 rounded-lg shadow-sm"
          />
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <h3 className="text-lg font-semibold text-gray-800 mb-3">Key Features</h3>
            <ul className="list-disc list-inside text-gray-600 space-y-2">
              <li>Direct payment creation and management</li>
              <li>Purchase orders to bill conversion</li>
              <li>Payment recording and tracking</li>
              <li>Bill editing and void functionality</li>
              <li>Batch tracking for inventory items</li>
              <li>PDF generation and document management</li>
              <li>Journal entry automation</li>
            </ul>
          </div>

        </div>
      </div>




      {/* Search and Filtering */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Search and Filtering Bills</h2>
        <p className="text-gray-600 mb-4">
          Efficiently locate and manage bills using comprehensive search and filtering options with multiple criteria.
        </p>

        <div className="mb-6">
          <img
            src="/images/purchase/payment-made/search-form.png"
            alt="Payment Made Search Dialog"
            className="w-full border border-gray-300 rounded-lg shadow-sm"
          />
        </div>

        <h3 className="text-lg font-semibold text-gray-800 mb-3">Advanced Search Dialog</h3>
        <p className="text-gray-600 mb-4">
          Use the comprehensive search dialog to find specific bills with multiple filter criteria:
        </p>
        <div className="grid md:grid-cols-2 gap-6 mb-6">
          <div>
            <p className="text-gray-600 mb-2"><strong>Identification Filters:</strong></p>
            <ul className="list-disc list-inside text-gray-600 space-y-1 text-sm">
              <li><strong>Payment #:</strong> Search by specific bill number</li>
              <li><strong>Reference #:</strong> Find by supplier's invoice reference</li>
              <li><strong>Bill Number:</strong> Filter by product batch numbers</li>
              <li><strong>Paid Through :</strong> Filter by payment methods</li>
            </ul>
          </div>
          <div>
            <p className="text-gray-600 mb-2"><strong>Date and Entity Filters:</strong></p>
            <ul className="list-disc list-inside text-gray-600 space-y-1 text-sm">
              <li><strong>Payment Date Range:</strong> Filter by bill creation dates</li>

            </ul>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-6 mb-6">
          <div>
            <p className="text-gray-600 mb-2"><strong>Status and Branch Filters:</strong></p>
            <ul className="list-disc list-inside text-gray-600 space-y-1 text-sm">
              <li><strong>Branch:</strong> Filter by organizational branch</li>
              <li><strong>Supplier</strong> Combine filters for precise results</li>
              <li><strong>Search and Cancel:</strong> Execute search or clear criteria</li>
            </ul>
          </div>

        </div>

        <h3 className="text-lg font-semibold text-gray-800 mb-3">Quick Search with Search Icon</h3>
        <p className="text-gray-600 mb-4">
          Use the search icon (🔍) in the top-right corner for instant bill filtering:
        </p>
        <ul className="list-disc list-inside text-gray-600 space-y-2 mb-6">
          <li><strong>Real-time Search:</strong> Filter bills as you type</li>
          <li><strong>Multi-field Search:</strong> Searches across bill numbers, suppliers, and references</li>
          <li><strong>Quick Access:</strong> No separate forms needed for basic searches</li>
          <li><strong>Immediate Results:</strong> Instant table filtering and updates</li>
        </ul>

        <h3 className="text-lg font-semibold text-gray-800 mb-3">Search Use Cases</h3>
        <div className="grid md:grid-cols-2 gap-6 mb-6">
          <div>
            <p className="text-gray-600 mb-2"><strong>Payment Management:</strong></p>
            <ul className="list-disc list-inside text-gray-600 space-y-1 text-sm">
              <li>Find overdue bills by due date range</li>
              <li>Locate bills ready for payment processing</li>
              <li>Search for specific supplier bills</li>
              <li>Filter by payment status</li>
            </ul>
          </div>
          <div>
            <p className="text-gray-600 mb-2"><strong>Reporting and Audit:</strong></p>
            <ul className="list-disc list-inside text-gray-600 space-y-1 text-sm">
              <li>Generate reports for specific date ranges</li>
              <li>Find bills for audit purposes</li>
              <li>Track bills by warehouse or branch</li>
              <li>Verify purchase order to bill conversions</li>
            </ul>
          </div>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <p className="text-sm text-blue-800">
            <strong>Search Tip:</strong> Use the advanced search dialog for complex queries with multiple criteria,
            and the quick search icon for simple, immediate filtering. Combine different search fields to narrow
            down results and find exactly the bills you need for efficient accounts payable management.
          </p>
        </div>
      </div>

      {/* Creating  Payment Made */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Creating Payment Made</h2>
        <p className="text-gray-600 mb-4">
          Bills can be created directly (when allowed) or generated from purchase orders. The bill creation
          process includes detailed product information, batch tracking, and financial calculations.
        </p>

        <div className="mb-6">
          <img
            src="/images/purchase/payment-made/payment-create.png"
            alt="Edit Bill Form"
            className="w-full border border-gray-300 rounded-lg shadow-sm"
          />
        </div>

        <h3 className="text-lg font-semibold text-gray-800 mb-3">Bill Header Information</h3>
        <div className="grid md:grid-cols-2 gap-6 mb-6">
          <div>
            <p className="text-gray-600 mb-2"><strong>Basic Details:</strong></p>
            <ul className="list-disc list-inside text-gray-600 space-y-1 text-sm">
              <li><strong>Supplier:</strong> Select vendor with search functionality</li>
              <li><strong>Branch:</strong> Organizational branch assignment</li>
              <li><strong>Currency:</strong> Link to originating PO</li>
            </ul>
          </div>
          <div>
            <p className="text-gray-600 mb-2"><strong>Additional Information:</strong></p>
            <ul className="list-disc list-inside text-gray-600 space-y-1 text-sm">
              <li><strong>Payment Date:</strong> Date of payment creation</li>
              <li><strong>Paid Through:</strong> Supplier's invoice reference</li>
              <li><strong>Payment Mode:</strong> Payment conditions</li>
              <li><strong>Bank Charge(if any):</strong> Transaction currency</li>
              <li><strong>Reference#:</strong> Delivery location</li>
            </ul>
          </div>
        </div>



        <h3 className="text-lg font-semibold text-gray-800 mb-3">Financial Calculations</h3>
        <ul className="list-disc list-inside text-gray-600 space-y-2 mb-6">
          <li><strong>Discount Management:</strong> Percentage or fixed amount discounts</li>
          <li><strong>Tax Calculations:</strong> Automatic tax computation</li>
          <li><strong>Sub Total and Total:</strong> Automatic amount calculations</li>
          <li><strong>Currency Handling:</strong> Multi-currency support</li>
        </ul>

        <h3 className="text-lg font-semibold text-gray-800 mb-3">Notes and Attachments</h3>
        <ul className="list-disc list-inside text-gray-600 space-y-2 mb-6">
          <li><strong>Notes Section:</strong> Additional bill information and comments</li>
          <li><strong>File Attachments:</strong> Upload supporting documents</li>
          <li><strong>File Size Limits:</strong> Maximum file size restrictions</li>
          <li><strong>Document Management:</strong> Organized file storage</li>
        </ul>
      </div>

      {/* Payment Made Detail View and Actions */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Payment Receive Detail View and Management</h2>
        <p className="text-gray-600 mb-4">
          The bill detail view provides comprehensive information and access to all bill management functions.
        </p>

        <div className="mb-6">
          <img
            src="/images/purchase/payment-made/detail.png"
            alt="Bill Detail View"
            className="w-full border border-gray-300 rounded-lg shadow-sm"
          />
        </div>

        <h3 className="text-lg font-semibold text-gray-800 mb-3">Action Buttons and Functions</h3>
        <div className="grid md:grid-cols-2 gap-6 mb-6">
          <div>
            <p className="text-gray-600 mb-2"><strong>Primary Actions:</strong></p>
            <ul className="list-disc list-inside text-gray-600 space-y-1 text-sm">
              <li><strong>Edit:</strong> Modify bill details (when allowed)</li>
              <li><strong>PDF/Print:</strong> Generate bill documents</li>
              <li><strong>Delete:</strong> Process payment against bill</li>
            </ul>
          </div>
        </div>

        <h3 className="text-lg font-semibold text-gray-800 mb-3">Payment Receive Information Display</h3>
        <div className="grid md:grid-cols-2 gap-6 mb-6">
          <div>
            <p className="text-gray-600 mb-2"><strong>Header Information:</strong></p>
            <ul className="list-disc list-inside text-gray-600 space-y-1 text-sm">
              <li>Payment  number and date</li>
              <li>Bank Charges</li>
              <li>Deposit To</li>
            </ul>
          </div>
          <div>
            <p className="text-gray-600 mb-2"><strong>Financial Summary:</strong></p>
            <ul className="list-disc list-inside text-gray-600 space-y-1 text-sm">
              <li>Sub total and tax amounts</li>
              <li>Total bill amount</li>
              <li>Remaining balance due</li>
              <li>Payment history and credits applied</li>
            </ul>
          </div>
        </div>
      </div>


      {/* Payment Edit and Integration */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Payment Made Edit and Tracking and Integration</h2>
        <p className="text-gray-600 mb-4">
          Track payment made relationships with payments, purchase orders, and view detailed transaction history.
        </p>

        <h3 className="text-lg font-semibold text-gray-800 mb-3">Payment Receive Edit</h3>
        <div className="mb-6">
          <img
            src="/images/purchase/payment-made/edit-form.png"
            alt="Payments Made Tab"
            className="w-full border border-gray-300 rounded-lg shadow-sm"
          />
        </div>

        <div className="grid md:grid-cols-2 gap-6 mb-6">
          <div>
            <p className="text-gray-600 mb-2"><strong>Payments Made Tab:</strong></p>
            <ul className="list-disc list-inside text-gray-600 space-y-1 text-sm">
              <li>Complete payment history for the bill</li>
              <li>Payment dates and reference numbers</li>
              <li>Payment modes and amounts</li>
              <li>Remaining balance tracking</li>
            </ul>
          </div>
          <div>
            <p className="text-gray-600 mb-2"><strong>Purchase Order Tab:</strong></p>
            <ul className="list-disc list-inside text-gray-600 space-y-1 text-sm">
              <li>Link to originating purchase order</li>
              <li>Purchase order details and status</li>
              <li>Order-to-bill conversion tracking</li>
              <li>Procurement audit trail</li>
            </ul>
          </div>
        </div>




        <h3 className="text-lg font-semibold text-gray-800 mb-3">Journal Entry Integration</h3>
        <div className="mb-6">
          <img
            src="/images/purchase/payment-made/journal.png"
            alt="Journal Entries"
            className="w-full border border-gray-300 rounded-lg shadow-sm"
          />
        </div>

        <p className="text-gray-600 mb-4">
          View automatic journal entries generated from bill transactions:
        </p>
        <div className="grid md:grid-cols-2 gap-6 mb-6">
          <div>
            <p className="text-gray-600 mb-2"><strong>Account Entries:</strong></p>
            <ul className="list-disc list-inside text-gray-600 space-y-1 text-sm">
              <li><strong>Accounts Payable:</strong> Credit entries for amounts owed</li>
              <li><strong>Tax Payable:</strong> Tax liability entries</li>
              <li><strong>Inventory Asset:</strong> Debit entries for received goods</li>
              <li><strong>Expense Accounts:</strong> Direct expense postings</li>
            </ul>
          </div>
          <div>
            <p className="text-gray-600 mb-2"><strong>Journal Features:</strong></p>
            <ul className="list-disc list-inside text-gray-600 space-y-1 text-sm">
              <li>Automatic posting upon bill confirmation</li>
              <li>Debit and credit balance verification</li>
              <li>Multi-currency support and conversion</li>
              <li>Audit trail for all journal postings</li>
            </ul>
          </div>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <p className="text-sm text-blue-800">
            <strong>Integration Note:</strong> Bills automatically generate journal entries that update
            accounts payable, inventory, and expense accounts. This ensures accurate financial reporting
            and maintains proper accounting records for all supplier transactions.
          </p>
        </div>
      </div>
    </div>
  );
};

export default PaymentMade;
